/**
 * Scan matériel capté partout sur le PDA, quel que soit l'élément qui a le focus.
 *
 * Relevé sur le V2s de Vallon (2026-11-02) : le focus restait sur le bouton « Lire un code »
 * touché avant ; les chiffres du clavier émulé n'arrivaient pas au champ de scan et l'Entrée
 * final « cliquait » ce bouton. Ici, un code terminé par Entrée est un scan (règles dans
 * scanDetector : tout s'accumule hors vrai champ texte, rafale rapide exigée dans un champ) :
 * l'Entrée est bloquée (aucun clic, aucun envoi de formulaire) et le code est transmis.
 * Une frappe lente dans un champ reste une frappe. Les champs des fenêtres de
 * saisie (création d'article…) gardent leur comportement. Le champ de scan du Mode Stock
 * est vidé : c'est ce cadre qui transmet le code, entier même si le focus a changé en route.
 */
import { useEffect, useRef } from 'react';
import { createScanDetector } from '@/features/pda/scanDetector';

const isEditable = (el: HTMLElement): el is HTMLInputElement | HTMLTextAreaElement =>
  el instanceof HTMLInputElement || el instanceof HTMLTextAreaElement;

/** Retire du champ les caractères du scan, tapés avant qu'on sache que c'en était un. */
const stripScanned = (el: HTMLInputElement | HTMLTextAreaElement, code: string) => {
  if (!el.value.endsWith(code)) return;
  const proto =
    el instanceof HTMLInputElement ? HTMLInputElement.prototype : HTMLTextAreaElement.prototype;
  Object.getOwnPropertyDescriptor(proto, 'value')?.set?.call(el, el.value.slice(0, -code.length));
  el.dispatchEvent(new Event('input', { bubbles: true }));
};

const IN_DIALOG = '[role="dialog"], [role="alertdialog"]';

export function usePdaScanCapture(onScan: (code: string) => void) {
  const onScanRef = useRef(onScan);
  onScanRef.current = onScan;

  useEffect(() => {
    const detector = createScanDetector();
    const onKeyDown = (event: KeyboardEvent) => {
      const target = event.target instanceof HTMLElement ? event.target : null;
      const sink = target?.dataset.scanSink !== undefined;
      if (target && !sink && isEditable(target) && target.closest(IN_DIALOG)) return;
      if (event.ctrlKey || event.altKey || event.metaKey) return;
      // Vrai champ texte : vitesse exigée (frappe humaine possible). Ailleurs : tout s'accumule.
      const typing = !!target && !sink && isEditable(target);
      const code = detector.feed(
        event.key,
        event.timeStamp || performance.now(),
        typing ? 'typing' : 'free',
      );
      if (!code) return;
      event.preventDefault();
      event.stopImmediatePropagation();
      // Champ de scan du Mode Stock : vidé, il n'envoie pas une seconde fois le code.
      if (target && isEditable(target)) stripScanned(target, sink ? target.value : code);
      onScanRef.current(code);
    };
    // Fenêtre, phase de capture : avant tout bouton, formulaire ou autre écouteur.
    window.addEventListener('keydown', onKeyDown, true);
    return () => window.removeEventListener('keydown', onKeyDown, true);
  }, []);

  useEffect(() => {
    // Jamais de focus laissé sur un bouton du cadre : le champ de scan le reprend.
    const onClick = () => {
      setTimeout(() => {
        const active = document.activeElement;
        if (
          (active instanceof HTMLButtonElement || active instanceof HTMLAnchorElement) &&
          !active.closest(IN_DIALOG)
        ) {
          active.blur();
        }
      }, 0);
    };
    document.addEventListener('click', onClick);
    return () => document.removeEventListener('click', onClick);
  }, []);
}
